<template>
  <base-entity-card
    :id="id"
    titleName="настроек сайта"
    :saveFunc="save"
    :loading="loading"
    @closed="restoreEntity"
    @opened="loadData"
    :oneColumn="false"
  >
    <v-form ref="form" v-model="isValid">
      <v-alert
        type="error"
        outlined
        v-if="errors.length > 0"
      >
        Исправьте ошибки и повторите сохранение:
        <ul>
          <li v-for="item of errors" :key="'er' + item">{{item}}</li>
        </ul>
      </v-alert>
      <v-card>
        <v-card-title>00. Привязка настроек</v-card-title>
        <v-card-text>
          <v-autocomplete
            label="Сайт *"
            v-model="entity.site"
            :items="sitesSelectData"
            clearable
            filled
            :rules="[v => !!v || 'Выберите сайт.']"
          ></v-autocomplete>
          <v-autocomplete
            label="Партнер *"
            v-model="entity.partner"
            :items="partnersSelectData"
            clearable
            filled
            :rules="[v => !!v || 'Выберите партнера.']"
          ></v-autocomplete>
          <v-switch
            v-model="entity.isActive"
            :label="`Статус настроек: ${entity.isActive ? 'активно': 'черновик'}`"
          ></v-switch>
        </v-card-text>
      </v-card>

      <v-card class="mt-7"> <!-- Блок "Сотрудники" -->
        <v-card-title>
          01. Сотрудники
          <v-spacer></v-spacer>
          <v-switch
            v-model="entity.specialistLayout.isActive"
          ></v-switch>
        </v-card-title>
        <v-card-text v-if="entity.specialistLayout.isActive">
          <v-autocomplete
            label="Отображаемые сотрудники"
            v-model="entity.specialistLayout.specialists"
            multiple
            small-chips
            deletable-chips
            filled
            :items="specialists"
          ></v-autocomplete>
        </v-card-text>
      </v-card>

      <v-card class="mt-7"> <!-- Блок "Отзывы" -->
        <v-card-title>
          02. Отзывы клиентов
          <v-spacer></v-spacer>
          <v-switch
            v-model="entity.reputationLayout.isActive"
          ></v-switch>
        </v-card-title>
        <v-card-text v-if="entity.reputationLayout.isActive">
          <v-autocomplete
            label="Отображаемые отзывы"
            v-model="entity.reputationLayout.testimonials"
            multiple
            small-chips
            deletable-chips
            filled
            :items="testimonials"
          ></v-autocomplete>
        </v-card-text>
      </v-card>

      <v-card class="mt-7"> <!-- Блок "Портфолио" -->
        <v-card-title>
          03. Портфолио
          <v-spacer></v-spacer>
          <v-switch
            v-model="entity.portfolioLayout.isActive"
          ></v-switch>
        </v-card-title>
        <v-card-text v-if="entity.portfolioLayout.isActive">
          <v-switch
            v-model="entity.portfolioLayout.isSquareFormat"
            :label="`Instagram-формат: ${entity.portfolioLayout.isSquareFormat ? 'да' : 'нет'}`"
          ></v-switch>
          <v-autocomplete
            label="Отображаемые работы"
            v-model="entity.portfolioLayout.portfolios"
            multiple
            small-chips
            deletable-chips
            filled
            :items="portfolios"
          ></v-autocomplete>
        </v-card-text>
      </v-card>

      <v-card class="mt-7"> <!-- Блок "Бонус" -->
        <v-card-title>
          04. Бонус
          <v-spacer></v-spacer>
          <v-switch
            v-model="entity.bonusLayout.isActive"
          ></v-switch>
        </v-card-title>
        <v-card-text v-if="entity.bonusLayout.isActive">
          <v-switch
            v-model="entity.bonusLayout.isTurnOn"
            :label="`Бонус при комплексном ремонте: ${entity.bonusLayout.isTurnOn ? 'включено': 'выключено'}`"
          ></v-switch>
          <template v-if="entity.bonusLayout.isTurnOn">
            <v-data-table
              :items="entity.bonusLayout.bonuses"
              :headers="bonusHeaders"
              hide-default-footer
            >
              <template v-slot:[`item.actions`]="{ item }">
                <site-page-bonus-card 
                  @input="saveBonus($event)"
                  :value="item"
                  :key="item._id"
                ></site-page-bonus-card>
                <v-btn icon @click="deleteBonus(item._id)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
            <div class="d-flex pt-2">
              <v-spacer></v-spacer>
              <site-page-bonus-card @input="saveBonus($event)"></site-page-bonus-card>
              <v-spacer></v-spacer>
            </div>
          </template>
        </v-card-text>
      </v-card>

      <v-card class="mt-7"> <!-- Блок "Общие данные" -->
        <v-card-title>
          05. Общие данные
          <v-spacer></v-spacer>
          <v-switch
            v-model="entity.commonLayout.isActive"
          ></v-switch>
        </v-card-title>
        <v-card-text v-if="entity.commonLayout.isActive">
          <v-autocomplete
            label="Прайс-лист *"
            v-model="entity.commonLayout.price"
            :items="pricesSelectData"
            clearable
            filled
            :rules="[v => !!v || 'Выберите прайс-лист.']"
          ></v-autocomplete> 

          <v-switch
            v-model="entity.commonLayout.manager.isActive"
            :label="`Руководитель: ${entity.commonLayout.manager.isActive ? 'указан': 'не указан'}`"
          ></v-switch>
          <template v-if="entity.commonLayout.manager.isActive">
            <v-text-field
              v-model="entity.commonLayout.manager.name"
              label="Имя руководителя"
              filled
            ></v-text-field>
            <v-text-field
              v-model="entity.commonLayout.manager.nameWhom"
              label="Имя руководителя (кого)"
              filled
            ></v-text-field>
            <v-text-field
              v-model="entity.commonLayout.manager.nameWhom1"
              label="Имя руководителя (кем)"
              filled
            ></v-text-field>
          </template>

          <v-switch
            v-model="entity.commonLayout.office.isActive"
            :label="`Офис: ${entity.commonLayout.office.isActive ? 'указан': 'не указан'}`"
          ></v-switch>
          <template v-if="entity.commonLayout.office.isActive">
            <v-text-field
              v-model="entity.commonLayout.office.address"
              label="Адрес офиса (улица и дом)"
              filled
            ></v-text-field>
          </template>

          <v-text-field
            v-model="entity.commonLayout.email"
            label="Email"
            filled
          >
          </v-text-field>

          <v-text-field
            v-model="entity.commonLayout.instagramUrl"
            label="Instagram"
            filled
          >
          </v-text-field>

          <v-text-field
            v-model="entity.commonLayout.objectsPerYear"
            label="Среднее число объектов (в год)"
            filled
            :rules="[v => isNumber(v) || 'Введите корректное число.']"
          >
          </v-text-field>

          <v-autocomplete
            label="Фотогалерея - Команда в деле"
            v-model="entity.commonLayout.teamPhotosGallery"
            :items="portfolios"
            clearable
            filled
          ></v-autocomplete>
        </v-card-text>
      </v-card>

    </v-form>
  </base-entity-card>
</template>

<script>
const MongoID = require('mongoose').Types.ObjectId;
import SiteEntityMixin from '../../../models/SiteEntityMixin';
import SitePageBonusCard from '../site_pages/SitePageBonusCard';

export default {
  name: 'site-settings-card',

  components: {
    SitePageBonusCard
  },

  mixins: [SiteEntityMixin],

  data() {
    return {
      errors: [],
      tag: 'settings',
      bonusHeaders: [
        {
          text: 'Заголовок',
          value: 'name'
        },
        {
          text: 'Действия',
          value: 'actions'
        }
      ],
      specialistList: [],
      testimonialList: [],
      portfolioList: []
    }
  },

  

  methods: {
    getDefaultEntity() {
      return {
        site: null,
        partner: null,
        isActive: false,
        specialistLayout: {
          isActive: false,
          specialists: []
        },
        portfolioLayout: {
          isActive: false,
          isSquareFormat: false,
          portfolios: []
        },
        reputationLayout: {
          isActive: false,
          testimonials: []
        },
        bonusLayout: {
          isActive: false,
          isTurnOn: true,
          bonuses: []
        },
        commonLayout: {
          isActive: false,
          price: null,
          manager: {
            isActive: false,
            name: '',
            nameWhom: '',
            nameWhom1: ''
          },
          office: {
            isActive: false,
            address: ''
          },
          objectsPerYear: 60,
          teamPhotosGallery: null,
          email: '',
          instagramUrl: ''
        },
        
      }
    },

    // Загрузка списков
    async loadData() {
      if (this.entity.site) {
        this.specialistList = await this.$store.dispatch('sites/getEntitiesList', {tag: 'specialist', site: this.entity.site});
        this.testimonialList = await this.$store.dispatch('sites/getEntitiesList', {tag: 'testimonial', site: this.entity.site});
        this.portfolioList = await this.$store.dispatch('sites/getEntitiesList', {tag: 'portfolio', site: this.entity.site});
      }
    },

     // Бонусы
    saveBonus(obj) {
      if (obj._id) {
        let index = this.entity.bonusLayout.bonuses.findIndex(x => x._id == obj._id);
        this.entity.bonusLayout.bonuses.splice(index, 1, obj);
      } else {
        obj._id = String(new MongoID());
        this.entity.bonusLayout.bonuses.push(obj);
      }
    },
    deleteBonus(id) {
      let index = this.entity.bonusLayout.bonuses.findIndex(x => x._id == id);
      this.entity.bonusLayout.bonuses.splice(index, 1);
    },

    // Validate
    validateFunc() {
      this.errors = [];
      
      if (this.entity.specialistLayout.isActive) {
        if (this.entity.specialistLayout.specialists.length < 4) {
          this.errors.push('Добавьте не менее 4-х сотрудников.');
        }
      }

      if (this.entity.portfolioLayout.isActive) {
        if (this.entity.portfolioLayout.portfolios.length < 3) {
          this.errors.push('Добавьте не менее 3-х работ в портфолио.');
        }
      }

      if (this.entity.reputationLayout.isActive) {
        if (this.entity.reputationLayout.testimonials.length < 5) {
          this.errors.push('Добавьте не менее 5-ти отзывов от клиентов.');
        }
      }

      if (this.entity.bonusLayout.isActive && this.entity.bonusLayout.isTurnOn) {
        if (this.entity.bonusLayout.bonuses.length != 2) {
          this.errors.push('В блоке бонусов должно быть ровно 2 бонуса на выбор.');
        }
      }

      if (this.errors.length > 0) this.scrollUp();
      
      return this.errors.length == 0;
    }
  },

  watch: {
    async ['entity.site'](newVal) {
      this.specialistList = await this.$store.dispatch('sites/getEntitiesList', {tag: 'specialist', site: newVal});
      this.testimonialList = await this.$store.dispatch('sites/getEntitiesList', {tag: 'testimonial', site: newVal});
      this.portfolioList = await this.$store.dispatch('sites/getEntitiesList', {tag: 'portfolio', site: newVal});
    }
  },

  computed: {
    partnersSelectData() {
      return this.$store.getters['users/partnerUsers'].map(x => ({
        value: x._id,
        text: x.fullName
      }));
    },
    specialists() {
      return this.specialistList
        .filter(x => x.site == this.entity.site)
        .map(x => ({
          text: `${x.fullName} (${x.position})`,
          value: x._id
        }));
    },
    testimonials() {
      return this.testimonialList
        .filter(x => x.site == this.entity.site)
        .map(x => ({
          text: `${x.objectName} (${x.customerName})`,
          value: x._id
        }));
    },
    portfolios() {
      return this.portfolioList
        .filter(x => x.site == this.entity.site)
        .map(x => ({
          text: x.name,
          value: x._id
        }));
    },
    pricesSelectData() {
      return this.$store.getters['prices/pricesSelectData'];
    }
  }
}
</script>